import { addToast } from "components/ToastContainer/ToastStore";
import { ProfileData } from "pages/Profile/types";
import {
    ChangeEvent,
    FormEvent,
    useCallback,
    useEffect,
    useState,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAppDispatch } from "slice";
import { setUser, useGetUserProfileDataQuery } from "slice/user";
import { loginUser, useLoginMutation } from "slice/userAuth";
import { validations } from "utils/validations";
import { LoginState } from "./types";

export const useLogin = () => {
    const [login, { isLoading, data: loginData, error }] = useLoginMutation(),
        [inputs, setInputs] = useState({
            email: "",
            password: "",
        }),
        [inputErrors, setInputErrors] = useState({
            email: "",
            password: "",
        });
    const dispatch = useAppDispatch(),
        navigateTo = useNavigate(),
        state = useLocation().state as LoginState;

    const [userId, setUserId] = useState("");

    const { data, isFetching: isFetchingProfileData } =
        useGetUserProfileDataQuery(
            {
                user_id: userId,
            },
            {
                skip: userId === "",
                refetchOnMountOrArgChange: true,
            }
        );

    const profileData: ProfileData = data?.data.attributes;

    const handleInputChange = useCallback(
        (e: ChangeEvent<HTMLInputElement>) => {
            const { name, value } = e.target;
            setInputs((prev) => ({
                ...prev,
                [name]: value,
            }));

            setInputErrors((prev) => ({
                ...prev,
                [name]: validations(name, value),
            }));
        },
        []
    );

    const checkForErrors = () => {
        let flag = true;
        Object.keys(inputs).forEach((key) => {
            const error = validations(key, inputs[key as keyof typeof inputs]);
            if (error !== "") {
                flag = false;
            }
            setInputErrors((prev) => ({
                ...prev,
                [key]: error,
            }));
        });

        return flag;
    };

    const onSubmit = (event: FormEvent) => {
        event.preventDefault();
        if (checkForErrors()) {
            login({ ...inputs, grant_type: "password" });
        }
    };

    useEffect(() => {
        if (profileData) {
            dispatch(
                setUser({
                    ...profileData,
                })
            );
        }
    }, [dispatch, profileData]);

    useEffect(() => {
        if (loginData) {
            dispatch(loginUser(loginData));
            setUserId(loginData.user_id);
        }
    }, [loginData, dispatch, navigateTo]);

    useEffect(() => {
        if (error) {
            addToast({
                type: "error",
                text: "Invalid username/password. Please try again.",
            });
        }
    }, [error]);

    useEffect(() => {
        if (state?.toast) {
            addToast(state.data);
            window.history.replaceState({}, document.title);
        }
    }, [state]);

    return {
        inputs,
        onSubmit,
        handleInputChange,
        isLoading,
        isFetchingProfileData,
        inputErrors,
    };
};
